import { Injectable, computed, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, httpResource } from '@angular/common/http';
import { Observable, lastValueFrom, filter, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { StatisticsService, SalesStatistics } from './statistics.service';
import { StorefrontService } from './storefront.service';
import { TokenStorageService } from './token-storage.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private apiUrl = environment.apiUrl;
  // ── State ────────────────────────────────────────────────────────────────
  // The company is a resource keyed on the authenticated company id. It reloads
  // itself on login and stays idle on storefront pages and while logged out,
  // where the URL is undefined. Loading and error are part of the store on
  // purpose: `company$` used to filter out the initial null, so a consumer could
  // not tell "still loading" from "failed" — and a failed load only got logged,
  // leaving the UI on its skeleton forever.
  private readonly companyResource = httpResource<Company>(
    () => {
      if (this.storefrontService.isStorefront()) return undefined;
      const id = this.authService.publicCompanyId();
      return id ? `${this.apiUrl}/companies/${id}` : undefined;
    },
    { defaultValue: null as unknown as Company },
  );

  private readonly _statistics = signal<SalesStatistics | null>(null);

  // value() throws while the resource is in its error state; hasValue() doesn't. Without
  // this, a failed load threw out of every reader of company() - `loaded` and the route
  // guards included - instead of showing up as `error`.
  readonly company = computed(() =>
    this.companyResource.hasValue() ? (this.companyResource.value() ?? null) : null,
  );
  readonly statistics = this._statistics.asReadonly();
  readonly loading = this.companyResource.isLoading;
  readonly error = computed(() =>
    this.companyResource.error() ? 'Unternehmensdaten konnten nicht geladen werden.' : null,
  );
  /** True once a load attempt finished, regardless of outcome. */
  readonly loaded = computed(() => this.company() !== null || this.error() !== null);

  private get publicCompanyId(): string | null {
    return this.company()?.public_id ?? this.authService.publicCompanyId();
  }

  /**
   * @deprecated Compatibility bridge for components that still subscribe.
   * Read the `company` signal instead — it also exposes loading and error.
   */
  public company$ = toObservable(this.company).pipe(
    filter((company): company is Company => company !== null),
  );

  /** @deprecated See {@link company$}. */
  public statistics$ = toObservable(this._statistics).pipe(
    filter((stats): stats is SalesStatistics => stats !== null),
  );

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private statisticsService: StatisticsService,
    private storefrontService: StorefrontService,
    private tokenStorage: TokenStorageService,
    private logger: LoggerService,
  ) {}

  /** @deprecated The resource reacts to login on its own. */
  initializeAfterLogin(): void {
    this.companyResource.reload();
  }

  /** Re-read the company, e.g. after completing the profile. */
  public async reloadCompanyData(): Promise<void> {
    this.companyResource.reload();
  }

  private refreshCompany(): void {
    this.companyResource.reload();
  }

  private setCompany(company: Company): void {
    this.companyResource.set(company);
  }

  updateCompany(company: CompanyStripeAccountChange): Observable<CompanyStripeAccountChange> {
    return this.http.patch<CompanyStripeAccountChange>(`${this.apiUrl}/companies`, company);
  }

  getCompany(public_id: string): Observable<Company> {
    return this.http.get<Company>(`${this.apiUrl}/companies/${public_id}`);
  }

  // Get current company data using the existing /api/companies endpoint
  getCurrentCompanyFromAuth(): Observable<Company> {
    const publicCompanyId = this.tokenStorage.getPublicCompanyId();
    if (!publicCompanyId) {
      throw new Error('No company ID found');
    }
    return this.getCompany(publicCompanyId);
  }

  getCurrentCompany(): Company | null {
    return this.company();
  }

  async loadInitialStatistics(): Promise<void> {
    try {
      const data = await lastValueFrom(this.statisticsService.getSalesStatistics());
      this._statistics.set(data);
    } catch (error: any) {
      this.logger.error('Error loading statistics: ', error);
      if (error.status === 401) {
        this.authService.logout();
      }
    }
  }

  getCurrentStatistics(): SalesStatistics | null {
    return this._statistics();
  }

  updateStatistics(statistics: SalesStatistics): void {
    this._statistics.set(statistics);
  }

  /** Member payout account / network debit account; the company reloads afterwards. */
  updateBankAccount(data: BankAccountUpdate): Observable<BankAccount> {
    return this.http
      .put<BankAccount>(`${this.apiUrl}/company/bank-account`, data)
      .pipe(tap(() => this.refreshCompany()));
  }

  /** Withdraw a member's IBAN change that still waits for its e-mail confirmation. */
  cancelBankAccountChange(): Observable<BankAccount> {
    return this.http
      .delete<BankAccount>(`${this.apiUrl}/company/bank-account/pending`)
      .pipe(tap(() => this.refreshCompany()));
  }

  /** Public: the token from the confirmation e-mail; works without being logged in. */
  confirmBankAccountChange(token: string): Observable<BankAccount> {
    return this.http.post<BankAccount>(`${this.apiUrl}/public/bank-account/confirm`, { token });
  }

  updateCompanyDetails(companyDetails: CompanyDetailsUpdate): Observable<Company> {
    if (this.publicCompanyId === null) {
      return throwError(() => new Error('No company ID available'));
    }

    companyDetails.public_id = this.publicCompanyId;

    return this.http
      .patch<Company>(`${this.apiUrl}/companies`, companyDetails)
      .pipe(tap((updatedCompany) => this.setCompany(updatedCompany)));
  }

  /** Adopt a company object the caller already has, without a round trip. */
  updateCompanyState(company: Company): void {
    this.setCompany(company);
  }

  changeCompanyPassword(passwordData: CompanyPasswordChange): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/change-password`, passwordData);
  }

  uploadCompanyLogo(file: File): Observable<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ImageUploadResponse>(`${this.apiUrl}/companies/upload-logo`, formData)
      .pipe(tap(() => this.refreshCompany()));
  }

  uploadCompanyBanner(file: File): Observable<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ImageUploadResponse>(`${this.apiUrl}/companies/upload-banner`, formData)
      .pipe(tap(() => this.refreshCompany()));
  }

  deleteCompanyImage(imageType: 'logo' | 'banner'): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this.apiUrl}/companies/delete-image`, {
        body: { image_type: imageType },
      })
      .pipe(tap(() => this.refreshCompany()));
  }

  getApiBaseUrl(): string {
    return this.apiUrl;
  }

  dismissSetupTask(taskId: string): Observable<Company> {
    return this.http
      .patch<Company>(`${this.apiUrl}/companies/dismiss-setup-task`, { task_id: taskId })
      .pipe(tap((updatedCompany) => this.setCompany(updatedCompany)));
  }

  unblockPublicRedemption(): Observable<{
    message: string;
    failed_pin_attempts: number;
    redemption_blocked: boolean;
  }> {
    return this.http
      .post<{
        message: string;
        failed_pin_attempts: number;
        redemption_blocked: boolean;
      }>(`${this.apiUrl}/companies/unblock-redemption`, {})
      .pipe(tap(() => this.refreshCompany()));
  }
}

export interface Company {
  first_name: string;
  last_name: string;
  name: string; // Shop/brand name (customer-facing)
  company_name?: string; // Official company name for invoices
  vat_id?: string; // UST-ID/VAT ID for Austrian companies
  email: string;
  description: string;
  // Billing address (for invoices)
  billing_street?: string;
  billing_city?: string;
  billing_zip?: string;
  billing_country?: string;
  // Business address (for storefront/vouchers)
  business_street?: string;
  business_city?: string;
  business_zip?: string;
  business_country?: string;
  use_billing_as_business?: boolean;
  phone: string;
  website: string;
  suburl: string;
  public_id: string;
  created_at: string;
  stripe_account_id: string;
  is_stripe_onboarding_success: boolean;
  is_storefront_enabled: boolean;
  logo_image_path?: string;
  banner_image_path?: string;
  voucher_app_title?: string;
  voucher_app_description?: string;
  minimum_voucher_amount?: number; // Amount in cents
  voucher_app_primary_color?: string; // Hex color e.g. #FF0000
  voucher_app_text_color?: string; // Hex color e.g. #FFFFFF
  active_money_design_id?: string | null; // the design money vouchers print on
  active_product_design_id?: string | null; // the design product vouchers print on
  redemption_pin?: string; // 4-6 digit PIN for employee redemption
  failed_pin_attempts?: number; // Track failed PIN attempts
  redemption_blocked?: boolean; // Block after 3 failed attempts
  onboarding_step_completed?: number;
  is_profile_completed?: boolean;
  company_covers_customer_fee?: boolean; // Whether company covers the 0.99€ customer fee
  require_qr_code_on_voucher?: boolean; // the QR code is a required element of every design
  require_expiry_on_voucher?: boolean; // the expiry date is a required text of every design
  voucher_expiry_printed?: boolean; // the active designs print the expiry date (model property)
  voucher_date_guaranteed?: boolean; // required on every design and printed by the active ones
  voucher_validity_years?: number | null; // Validity of new vouchers; null = legal minimum
  legal_min_validity_years?: number; // Shortest validity for the current designs
  legal_min_validity_years_with_date?: number; // ... with the expiry date printed
  legal_min_validity_years_without_date?: number; // ... without it (Italy: 10 years)
  voucher_products_enabled?: boolean; // Whether company can use voucher products (default: false)
  money_voucher_enabled?: boolean; // Whether company can sell money vouchers (default: true)
  fixed_voucher_amounts_enabled?: boolean; // Sells money vouchers at preset amounts too
  fixed_voucher_amounts?: number[] | null; // The preset amounts in cents, ascending (kept while off)
  notify_on_sale_email?: boolean; // E-mail on every sale (default: true, opt-out in settings)
  dismissed_setup_tasks?: string[]; // Task IDs dismissed by the user on the setup checklist
  app_config_customized?: boolean; // Set when app config fields are saved via settings
  design_customized?: boolean; // Set when design/color is saved via settings
  voucher_design_edited?: boolean; // a design was changed in the editor or uploaded (checklist task)
  // The shop was seen on the website (checklist task): the plugin loaded there, or a
  // visitor came to the storefront through a link from there. Set by the backend.
  integration_detected_at?: string | null;
  integration_method?: 'plugin' | 'link' | null;
  integration_url?: string | null; // the linking page, or the origin the plugin ran on
  integration_last_seen_at?: string | null;
  first_login_at?: string | null; // Set once on the very first login, never updated
  last_login_at?: string | null; // Updated on every login and token refresh
  has_password?: boolean; // false for Google-SSO-only accounts (no password set yet)
  has_google?: boolean; // true when a Google identity is linked to the account
  // Account mode for Werbegemeinschaft / Franchise
  is_group_admin?: boolean; // Opted-in as central/admin account
  members_can_see_reporting?: boolean; // Central: allow members to see group stats
  group_company_id?: string | null; // Central company public_id (null if not a member)
  group_membership_status?: string | null;
  group_locked_at?: string | null; // Set once the first membership existed: the network can no longer be switched off
  account_type?: 'shop' | 'member'; // member: joined a network by invite, no Stripe, no selling
  fee_rate?: number; // Schenkwerk's share for new sales (a network may have its own)
  customer_fee_cents?: number; // service fee per order for new sales
  has_bank_account?: boolean;
  bank_iban_masked?: string | null; // e.g. "DE89 **** **** **** **30 00" - never the full IBAN
  bank_account_holder?: string | null;
  bank_bic?: string | null;
  bank_iban_changed_at?: string | null;
  pending_bank_iban_masked?: string | null; // member IBAN change awaiting e-mail confirmation
  pending_bank_expires_at?: string | null;
  is_member?: boolean; // Derived: active member of another group
  active_member_count?: number; // Derived: active members in this company's group
  member_of_company_name?: string | null; // Derived: central company name
  member_can_see_reporting?: boolean | null; // Derived: central allows reporting
  group_company_suburl?: string | null; // Derived: central company's storefront suburl
}

export interface BankAccountUpdate {
  iban: string;
  account_holder: string;
  bic?: string | null;
}

export interface BankAccount {
  has_bank_account: boolean;
  iban_masked: string | null;
  account_holder: string | null;
  bic: string | null;
  changed_at: string | null;
  pending_iban_masked: string | null;
  pending_expires_at: string | null;
}

export interface CompanyStripeAccountChange {
  public_id: string;
  is_stripe_onboarding_success: boolean;
}

export interface CompanyDetailsUpdate {
  public_id: string;
  first_name?: string;
  last_name?: string;
  company_covers_customer_fee?: boolean;
  name?: string; // Shop/brand name
  company_name?: string; // Official company name for invoices
  vat_id?: string; // UST-ID/VAT ID for Austrian companies
  // Billing address (for invoices)
  billing_street?: string;
  billing_city?: string;
  billing_zip?: string;
  billing_country?: string;
  // Business address (for storefront/vouchers)
  business_street?: string;
  business_city?: string;
  business_zip?: string;
  business_country?: string;
  use_billing_as_business?: boolean;
  phone?: string;
  website?: string;
  suburl?: string;
  description?: string;
  is_storefront_enabled?: boolean;
  voucher_app_title?: string;
  voucher_app_description?: string;
  minimum_voucher_amount?: number; // Amount in cents
  voucher_app_primary_color?: string; // Hex color e.g. #FF0000
  voucher_app_text_color?: string; // Hex color e.g. #FFFFFF
  redemption_pin?: string; // 4-6 digit PIN for employee redemption
  require_qr_code_on_voucher?: boolean; // the QR code is a required element of every design
  require_expiry_on_voucher?: boolean; // the expiry date is a required text of every design
  voucher_validity_years?: number | null; // Validity of new vouchers; null = legal minimum
  voucher_products_enabled?: boolean; // Whether company can use voucher products (default: false)
  money_voucher_enabled?: boolean; // Whether company can sell money vouchers (default: true)
  fixed_voucher_amounts_enabled?: boolean;
  fixed_voucher_amounts?: number[]; // cents; the backend deduplicates and sorts
  notify_on_sale_email?: boolean; // E-mail on every sale (default: true, opt-out in settings)
  is_group_admin?: boolean;
  members_can_see_reporting?: boolean;
}

export interface CompanyPasswordChange {
  old_password: string;
  new_password: string;
}

export interface ImageUploadResponse {
  filename: string;
  file_path: string;
  message: string;
}
